import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon     from '@mui/icons-material/CancelOutlined';
import EmailOutlinedIcon      from '@mui/icons-material/EmailOutlined';
import VideoCallIcon          from '@mui/icons-material/VideoCall';
import LocationOnIcon         from '@mui/icons-material/LocationOn';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon       from '@mui/icons-material/InfoOutlined';
import { LocationType } from '../../types/event';

interface ApplySuccessDialogProps {
  open:         boolean;
  onClose:      () => void;
  locationType: LocationType;
  type?:        'apply' | 'cancel';
}

export const ApplySuccessDialog: React.FC<ApplySuccessDialogProps> = ({
  open,
  onClose,
  locationType,
  type = 'apply',
}) => {
  const isOnline = locationType === 'online';
  const isCancel = type === 'cancel';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {isCancel
            ? <CancelOutlinedIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            : <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 28 }} />
          }
          <Typography variant="h6" fontWeight={700}>
            {isCancel ? '参加をキャンセルしました' : '申込が完了しました！'}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          {/* キャンセル時のメッセージ */}
          {isCancel ? (
            <>
              <Alert
                severity="info"
                icon={<EmailOutlinedIcon fontSize="small" />}
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="body2" fontWeight={600}>
                  キャンセル確認メールをお送りします
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                  メールが届かない場合は、迷惑メールフォルダもご確認ください。
                </Typography>
              </Alert>

              <Alert
                severity="info"
                icon={<InfoOutlinedIcon fontSize="small" />}
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="body2" fontWeight={600}>
                  再申込について
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                  申込期限内であれば、イベント詳細ページから再度お申込いただけます。
                </Typography>
              </Alert>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#FFF8E1',
                  border: '1px solid',
                  borderColor: '#FFE082',
                }}
              >
                <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18, flexShrink: 0, mt: '1px' }} />
                <Typography variant="caption" color="text.secondary">
                  <strong>企業へキャンセルの旨が通知される場合があります。</strong>
                  ご不明な点は企業へ直接お問い合わせください。
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {/* 申込時：確認メール */}
              <Alert
                severity="success"
                icon={<EmailOutlinedIcon fontSize="small" />}
                sx={{ alignItems: 'flex-start' }}
              >
                <Typography variant="body2" fontWeight={600}>
                  申込確認メールをお送りします
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                  メールが届かない場合は、迷惑メールフォルダもご確認ください。
                </Typography>
              </Alert>

              {/* オンライン専用：会議URL案内 */}
              {isOnline && (
                <Alert
                  severity="info"
                  icon={<VideoCallIcon fontSize="small" />}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    会議URLのご確認方法
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                    Zoom / Google Meet 等の会議URLは<strong>メール</strong>でお送りします。
                    このイベントの詳細ページからも申込後に確認できます。
                  </Typography>
                </Alert>
              )}

              {/* オフライン・ハイブリッド：開催場所案内 */}
              {!isOnline && (
                <Alert
                  severity="info"
                  icon={<LocationOnIcon fontSize="small" />}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    開催場所・参加方法のご確認方法
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                    開催場所などの詳細は<strong>メール</strong>でお送りします。
                    このイベントの詳細ページからも確認できます。
                  </Typography>
                </Alert>
              )}

              {/* 企業からの連絡に関する注意 */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#FFF8E1',
                  border: '1px solid',
                  borderColor: '#FFE082',
                }}
              >
                <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18, flexShrink: 0, mt: '1px' }} />
                <Typography variant="caption" color="text.secondary">
                  <strong>企業からご連絡が届く場合があります。</strong>
                  登録済みのメールアドレスを定期的にご確認ください。
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button variant="contained" onClick={onClose} fullWidth>
          確認しました
        </Button>
      </DialogActions>
    </Dialog>
  );
};
